import * as React from 'react';
import {Toast} from './Toast';
import {ToastLayer} from './ToastLayer';
import type {ToastActionProps, ToastProps} from './types';

// Generate unique IDs for toasts
let toastIdCounter = 0;
const generateToastId = () => `toast-provider-id-${toastIdCounter++}`;

type ToastBaseOptions = {
  /**
   * Toast ID to use
   */
  id?: string;
  /**
   * The message to display in the toast
   */
  message: React.ReactNode;
  /**
   * The type of toast
   */
  type: ToastProps['type'];
  /**
   * The duration of the toast before auto-dismissing
   */
  duration?: ToastProps['duration'];
  /**
   * Controls whether the toast should automatically dismiss
   */
  autoDismiss?: ToastProps['autoDismiss'];
  /**
   * Where to place the toast (top or bottom)
   */
  placement?: ToastProps['placement'];
  /**
   * Callback to be called when the toast action is clicked
   */
  onDismiss?: () => void;
};

export type ToastOptions = ToastBaseOptions & ToastActionProps;

type ToastItem = ToastOptions & {
  id: string;
  isOpen: boolean;
};

type ToastContextType = {
  /**
   * Show a toast and return its ID for later dismissal
   */
  show: (options: ToastOptions) => string;
  /**
   * Update an existing toast by ID
   */
  update: (
    id: string,
    options: Partial<ToastBaseOptions> & ToastActionProps,
  ) => void;
  /**
   * Dismiss a specific toast by ID
   */
  dismiss: (id: string) => void;
  /**
   * Dismiss all active toasts
   */
  dismissAll: () => void;
};

const ToastContext = React.createContext<ToastContextType | null>(null);

export const ToastProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);

  const show = React.useCallback<ToastContextType['show']>(
    ({placement = 'bottom', ...options}) => {
      const id = generateToastId();
      const newToast: ToastItem = {
        ...options,
        id,
        isOpen: false,
        placement,
      };

      setToasts((prev) => [...prev, newToast]);

      /**
       * TODO: use startTransition when we migrate to React 19
       * We need firstly to render ToastLayer and then update children
       * so appearing animation is smooth and we do not affect render count
       * for RootLayer
       */
      requestAnimationFrame(() => {
        setToasts((prev) =>
          prev.map((toast) =>
            toast.id === id ? {...toast, isOpen: true} : toast,
          ),
        );
      });

      return id;
    },
    [],
  );

  const update = React.useCallback<ToastContextType['update']>(
    (id, options) => {
      setToasts((prev) =>
        prev.map((toast) =>
          toast.id === id
            ? {
                ...toast,
                ...options,
              }
            : toast,
        ),
      );
    },
    [],
  );

  const dismiss = React.useCallback<ToastContextType['dismiss']>((id) => {
    setToasts((prev) => {
      const newToasts = prev.map((toast) =>
        toast.id === id ? {...toast, isOpen: false} : toast,
      );

      setTimeout(() => {
        setToasts((v) => v.filter((toast) => toast.id !== id));
      }, 1000);

      return newToasts;
    });
  }, []);

  const dismissAll = React.useCallback(() => {
    setToasts([]);
  }, []);

  const handleToastDismiss = React.useCallback(
    (toast: ToastItem) => () => {
      dismiss(toast.id);
      toast.onDismiss?.();
    },
    [dismiss],
  );

  const value = React.useMemo(
    () => ({show, update, dismiss, dismissAll}),
    [show, update, dismiss, dismissAll],
  );

  const topToasts = React.useMemo(
    () => toasts.filter((t) => t.placement === 'top'),
    [toasts],
  );
  const bottomToasts = React.useMemo(
    () => toasts.filter((t) => t.placement === 'bottom'),
    [toasts],
  );

  const hasTopLayer = topToasts.length > 0;
  const hasBottomLayer = bottomToasts.length > 0;

  return (
    <ToastContext.Provider value={value}>
      {children}

      {hasTopLayer ? (
        <ToastLayer placement="top">
          {topToasts.map((toast) => (
            <Toast
              {...toast}
              key={toast.id}
              onDismiss={handleToastDismiss(toast)}
            >
              {toast.message}
            </Toast>
          ))}
        </ToastLayer>
      ) : null}

      {hasBottomLayer ? (
        <ToastLayer placement="bottom">
          {bottomToasts.map((toast) => (
            <Toast
              {...toast}
              key={toast.id}
              onDismiss={handleToastDismiss(toast)}
            >
              {toast.message}
            </Toast>
          ))}
        </ToastLayer>
      ) : null}
    </ToastContext.Provider>
  );
};

/**
 * Hook to show, dismiss, and manage toasts.
 *
 * Must be used within a ToastProvider.
 *
 * @example
 * ```tsx
 * const toast = useToast();
 *
 * // Show a toast and get its ID
 * const id = toast.show({
 *   message: 'Operation successful!',
 *   type: 'neutral',
 * });
 *
 * // Update an existing toast
 * toast.update(id, {
 *   message: 'Updated message',
 *   type: 'error',
 * });
 *
 * // Dismiss a specific toast
 * toast.dismiss(id);
 *
 * // Dismiss all toasts
 * toast.dismissAll();
 * ```
 */
export const useToast = (): ToastContextType => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
